'use client';

import { useState } from 'react';
import Link from 'next/link';
import type { ComponentProps, MouseEvent } from 'react';

type HoverPrefetchLinkProps = ComponentProps<typeof Link>;

/**
 * Link that prefetches on hover instead of on viewport entry.
 *
 * Next.js defaults to prefetching every Link that enters the viewport,
 * which can generate dozens of RSC requests on pages with many navigation
 * links. This wrapper disables viewport prefetching and restores default
 * (static) prefetching once the user hovers — still instant for users
 * but dramatically reduces background bandwidth.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/link#prefetch
 */
export function HoverPrefetchLink({
  onMouseEnter,
  ...props
}: HoverPrefetchLinkProps) {
  const [active, setActive] = useState(false);

  return (
    <Link
      {...props}
      prefetch={active ? null : false}
      onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
        setActive(true);
        onMouseEnter?.(e);
      }}
    />
  );
}
